/**
 * Localhost-only Payments UAT (sandbox01) override.
 *
 * Enabled when:
 * - running on localhost, and
 * - REACT_APP_PAYMENTS_UAT_LOCAL=true in .env.local, and
 * - payments-local.json exists at repo root (used by src/setupProxy.js for Basic auth)
 *
 * Browser calls go to /payments-uat/* (CRA proxy → sandbox01).
 * Optional actor override is read via GET /payments-local-config (no secrets).
 * Directory Bearer is minted via POST /payments-local-directory-token
 * (directory-dev.loanpal.services/Authorization/apikey; credentials stay on the proxy).
 */

export function isPaymentsUatLocalEnabled() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const isLocal =
    host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  if (!isLocal) return false;
  return (
    process.env.REACT_APP_PAYMENTS_UAT_LOCAL === "true" ||
    process.env.REACT_APP_PAYMENTS_UAT_LOCAL === "1"
  );
}

/** Path prefix for CRA setupProxy → sandbox01-api.goodleap.com */
export function paymentsUatLocalPath(apiPath) {
  const normalized = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
  return `/payments-uat${normalized}`;
}

/**
 * Optional actor from payments-local.json (localhost UAT only).
 * Returns { email, firstName, lastName } when email is set; otherwise null.
 */
export async function fetchPaymentsLocalActorOverride() {
  if (!isPaymentsUatLocalEnabled()) return null;
  try {
    const res = await fetch("/payments-local-config");
    if (!res.ok) return null;
    const data = await res.json();
    const actor = data?.actor;
    const email = typeof actor?.email === "string" ? actor.email.trim() : "";
    if (!email) return null;
    return {
      email,
      firstName:
        typeof actor.firstName === "string" ? actor.firstName.trim() : "",
      lastName: typeof actor.lastName === "string" ? actor.lastName.trim() : "",
    };
  } catch (err) {
    console.log("[payments-local] could not load actor override", err);
    return null;
  }
}

/**
 * Mint directory Bearer via CRA proxy (directory-dev).
 * Uses apiUsername/apiPassword (or directory.organizationId/apiKey) from payments-local.json.
 * Returns { token, refreshToken, unixTimeExpiresAt } or throws.
 */
export async function fetchPaymentsLocalDirectoryToken() {
  if (!isPaymentsUatLocalEnabled()) {
    throw new Error("Payments UAT local is not enabled");
  }
  const res = await fetch("/payments-local-directory-token", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: "{}",
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const err = new Error(
      (data && (data.error || data.message)) ||
        `Directory token request failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}
